import { Button } from 'UI';
import { FC, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { Bonus } from '../../bonuses';
import { SHORT_SCREEN_QUERY } from '../../layout';
import { BonusUnlockStatus } from '../../useBonusUnlocks';
import { BonusBadge } from '../BonusBadge';

type BonusCardProps = {
  bonus: Bonus;
  /** MVP members claim the bonus; everyone else is offered the upgrade. */
  isMember: boolean;
  isDarkMode?: boolean;
  status?: BonusUnlockStatus;
  error?: string;
  onUnlock: (bonus: Bonus) => void;
  className?: string;
};

const getUnlockLabel = (bonus: Bonus, isMember: boolean, status?: BonusUnlockStatus) => {
  if (!isMember) return 'Unlock with MVP';
  if ((bonus.unlock.kind === 'handoff' || bonus.unlock.kind === 'link') && status === 'unlocked') {
    return bonus.unlock.openLabel;
  }
  return 'Unlock';
};

/** A partner code with a copy button, shown once the bonus is unlocked. */
const CodeReveal: FC<{ code: string; note?: string }> = ({ code, note }) => {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;
    const id = setTimeout(() => setIsCopied(false), 2000);
    return () => clearTimeout(id);
  }, [isCopied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
    } catch {
      // Clipboard blocked: the code stays visible to copy by hand.
    }
  };

  return (
    <CodeBlock>
      <CodeChip>
        <code>{code}</code>
        <CopyButton type="button" onClick={copy}>
          {isCopied ? 'Copied' : 'Copy'}
        </CopyButton>
      </CodeChip>
      {note && <CodeNote>{note}</CodeNote>}
    </CodeBlock>
  );
};

/** certify's mega-menu bonus card, plus the two actions: unlock it, or read about it on its own site. */
const BonusCard: FC<BonusCardProps> = ({ bonus, isMember, isDarkMode, status, error, onUnlock, className }) => {
  const logo = isDarkMode && bonus.logoDark ? bonus.logoDark : bonus.logo;
  const memberStatus = isMember ? status : undefined;

  return (
    <Card className={className}>
      <Top>
        {logo ? (
          <Logo
            src={logo}
            alt={bonus.logoAlt ?? bonus.name}
            $isTall={bonus.isTallLogo}
            $invert={isDarkMode && bonus.invertLogoInDark}
            loading="lazy"
          />
        ) : (
          <Name>{bonus.name}</Name>
        )}
        <BonusBadge slots={bonus.slots} status={memberStatus} />
      </Top>
      {logo && <Name>{bonus.name}</Name>}
      <Blurb>{bonus.blurb}</Blurb>

      <Footer>
        <ValueRow>
          <Value>{bonus.value}</Value>
          <Detail>{isMember ? bonus.memberDetail ?? bonus.detail : bonus.detail}</Detail>
        </ValueRow>
        {bonus.unlock.kind === 'link' && bonus.unlock.code && memberStatus === 'unlocked' && (
          <CodeReveal code={bonus.unlock.code} note={bonus.unlock.codeNote} />
        )}
        <Actions>
          <UnlockButton
            variant="primary"
            size="small"
            onClick={() => onUnlock(bonus)}
            isLoading={memberStatus === 'unlocking'}
            disabled={memberStatus === 'unlocking'}>
            {getUnlockLabel(bonus, isMember, memberStatus)}
          </UnlockButton>
          {bonus.website && (
            <WebsiteLink href={bonus.website} target="_blank" rel="noopener noreferrer">
              Visit website ↗
            </WebsiteLink>
          )}
        </Actions>
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </Footer>
    </Card>
  );
};

export default BonusCard;

const Card = styled.article`
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  padding: 14px 16px;

  border: 1px solid var(--bm-border);
  border-radius: 16px;
  background: var(--bm-card);
  transition: transform 200ms ease, border-color 200ms ease, background-color 200ms ease, box-shadow 200ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: var(--bm-accent);
    background: var(--bm-card-hover);
    box-shadow: 0 10px 24px rgba(20, 24, 38, 0.12);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }

  ${SHORT_SCREEN_QUERY} {
    padding: 10px 14px;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Logo = styled.img<{ $isTall?: boolean; $invert?: boolean }>`
  height: 24px;
  width: auto;
  max-width: 150px;
  object-fit: contain;
  object-position: left center;

  ${({ $isTall }) =>
    $isTall &&
    css`
      height: 44px;
      margin: -10px 0;
    `}

  ${({ $invert }) =>
    $invert &&
    css`
      filter: brightness(0) invert(1);
    `}
`;

const Name = styled.h3`
  margin-top: 4px;
  font-size: 16px;
  font-weight: 800;
  line-height: 1.35;
  color: var(--bm-ink);

  ${Top} > & {
    margin-top: 0;
  }

  ${SHORT_SCREEN_QUERY} {
    margin-top: 2px;
  }
`;

const Blurb = styled.p`
  margin-top: 4px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--bm-body);

  ${SHORT_SCREEN_QUERY} {
    margin-top: 2px;
  }
`;

const Footer = styled.div`
  margin-top: auto;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${SHORT_SCREEN_QUERY} {
    padding-top: 6px;
  }
`;

const ValueRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 8px;
`;

const Value = styled.span`
  font-size: 12px;
  font-weight: 800;
  line-height: 1.5;
  color: var(--bm-accent);
`;

const Detail = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: var(--bm-muted);
`;

const Actions = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;

  ${SHORT_SCREEN_QUERY} {
    margin-top: 6px;
  }
`;

// The LMS small button, tightened for use inside a card: 28px tall, with IconButton's 8px corners.
const UnlockButton = styled(Button)`
  width: auto;
  flex-shrink: 0;
  white-space: nowrap;
  padding: 5px 16px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 16px;
`;

const WebsiteLink = styled.a`
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-decoration: none;
  color: var(--bm-body);
  transition: color 200ms ease;

  &:hover {
    color: var(--bm-accent);
  }
`;

const ErrorText = styled.p`
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--bm-error);
`;

const CodeBlock = styled.div`
  margin-top: 8px;
`;

const CodeChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 2px 2px 2px 10px;
  border: 1px dashed var(--bm-accent);
  border-radius: 8px;

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: var(--bm-ink);
  }
`;

const CopyButton = styled.button`
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 800;
  color: #ffffff;
  background: var(--bm-accent);
`;

const CodeNote = styled.p`
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--bm-muted);
`;
